import React, { useEffect, useRef, useState } from "react";
import { FaMicrophone, FaRegStopCircle, FaVolumeUp } from "react-icons/fa";
import { FiCopy, FiUpload } from "react-icons/fi";
import { IoIosSend } from "react-icons/io";
import { useHistory, useLocation } from "react-router-dom";
import { createErrorAlert, updateUser } from "../../redux/actions";
import { updatedUserLoader } from "../../redux/selectors";
import { UpdateUser } from "../../services";
import { apiRequest, setUser } from "../../helpers";
import { useDispatch } from "react-redux";
// import { toast } from "react-toastify";

const API_BASE = "https://aibackend.studycrowdai.us/v1";
const AUTH_TOKEN = "Bearer app-RwAMNt12y2YAKTZhyZNBUN0E";

export function ChatInterface({ credits, onSessionCreated, updateUser,
    updatedUserLoader }) {
    const navigate = useHistory();
    const location = useLocation();
    const user = JSON.parse(sessionStorage?.getItem("app_user"));
    const stopRef = useRef(false);
    const messageContainer = useRef();
    const dispatch = useDispatch()

    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [file, setFile] = useState(null);
    const [uploadedFileMeta, setUploadedFileMeta] = useState(null);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [speakingId, setSpeakingId] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [conversationId, setConversationId] = useState("");
    const [messages, setMessages] = useState([
        {
            id: "welcome",
            from: "ai",
            text: `Hi ${user?.first_name || user?.user_name}, how can I help you today?`,
            timestamp: new Date(),
        },
    ]);

    const params = new URLSearchParams(location.search);
    const userId = params.get("user_id") || params.get("userid");
    const sessionFromUrl = params.get("session_id");

    const scrollToBottom = () => {
        if (messageContainer.current) {
            messageContainer.current.scrollTop = messageContainer.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!userId) return;

        const searchParams = new URLSearchParams(location.search);
        const isNew = searchParams.get("new") === "1";
        const sessionFromUrl = searchParams.get("session_id");

        if (sessionFromUrl && !isNew) {
            setConversationId(sessionFromUrl);
            fetchMessages(sessionFromUrl);
            return;
        }

        setConversationId("");
        setMessages([
            {
                id: "welcome",
                from: "ai",
                text: `Hi ${user?.first_name || user?.user_name}, how can I help you today?`,
                timestamp: new Date(),
            },
        ]);
        setFile(null);
        setPreviewUrl(null);
        setUploadedFileMeta(null);
        setInput("");
        setTaskId(null);
        setIsLoading(false);
    }, [location.search, userId]);

    const fetchMessages = async (sessionId) => {
        try {
            const res = await fetch(
                `${API_BASE}/messages?user=${userId}&conversation_id=${sessionId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: AUTH_TOKEN,
                    },
                }
            );

            const response = await res.json();
            const data = response?.data || [];

            const formatted = [
                {
                    id: "welcome",
                    from: "ai",
                    text: `Hi ${user?.first_name || user?.user_name}, how can I help you today?`,
                    timestamp: new Date(),
                },
            ];

            data.forEach((msg) => {
                if (msg.answer === "Hello! How can I help you today?") return;

                const firstFile = msg.message_files?.[0] || null;

                if (msg.query) {
                    formatted.push({
                        id: `${msg.id}-user`,
                        from: "user",
                        text: msg.query,
                        file_url: firstFile?.url || null,
                        fileType: firstFile?.mime_type || null,
                        fileName: firstFile?.filename || null,
                        uploadFileId: firstFile?.upload_file_id || null,
                        timestamp: new Date(msg.created_at * 1000),
                    });
                }

                if (msg.status === "error") {
                    let errorText = msg.error || "Something went wrong. Please try again.";

                    if (errorText.includes("Run failed:")) {
                        errorText = "Unable to generate response";
                    }

                    formatted.push({
                        id: `${msg.id}-error`,
                        from: "ai",
                        text: errorText,
                        timestamp: new Date(msg.created_at * 1000),
                    });
                } else if (msg.answer) {
                    formatted.push({
                        id: `${msg.id}-ai`,
                        from: "ai",
                        text: msg.answer.replace(/<think>[\s\S]*?<\/think>/g, "").trim(),
                        timestamp: new Date(msg.created_at * 1000),
                    });
                }
            });

            setMessages(formatted);
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    const typeText = async (fullText, delay = 20) => {
        return new Promise((resolve) => {
            if (!fullText) {
                resolve();
                return;
            }

            let currentText = "";
            let i = 0;

            const interval = setInterval(() => {
                if (stopRef.current) {
                    clearInterval(interval);
                    resolve();
                    return;
                }

                currentText += fullText[i];

                setMessages((prev) => {
                    const updated = [...prev];
                    if (updated.length > 0) {
                        updated[updated.length - 1] = {
                            ...updated[updated.length - 1],
                            text: currentText,
                        };
                    }
                    return updated;
                });

                i++;
                scrollToBottom();

                if (i >= fullText.length) {
                    clearInterval(interval);
                    resolve();
                }
            }, delay);
        });
    };

    const handleMicClick = () => {
        const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();

        recognition.lang = "en-US";

        recognition.onresult = (event) => {
            const text = event.results[0][0].transcript;
            setInput(text);
        };

        recognition.start();
    };

    // const handleMicClick = async () => {
    //     if (!isRecording) {
    //         const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    //         const mediaRecorder = new MediaRecorder(stream);
    //         mediaRecorderRef.current = mediaRecorder;
    //         audioChunksRef.current = [];

    //         mediaRecorder.ondataavailable = (event) => {
    //             audioChunksRef.current.push(event.data);
    //         };

    //         mediaRecorder.onstop = handleAudioUpload;

    //         mediaRecorder.start();
    //         setIsRecording(true);
    //     } else {
    //         mediaRecorderRef.current.stop();
    //         setIsRecording(false);
    //     }
    // };

    const handleAudioUpload = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });

        const formData = new FormData();
        formData.append("file", audioBlob, "voice.webm");

        try {
            const res = await fetch(`${API_BASE}/audio-to-text`, {
                method: "POST",
                headers: {
                    Authorization: AUTH_TOKEN,
                },
                body: formData,
            });

            const data = await res.json();

            setInput(data?.text || "");

        } catch (err) {
            console.error("Audio upload error:", err);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };


    const handleSpeak = (text, id) => {
        speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);

        utterance.onend = () => {
            setSpeakingId(null);
        };

        setSpeakingId(id);
        speechSynthesis.speak(utterance);
    };

    const handleStopSpeak = () => {
        speechSynthesis.cancel();
        setSpeakingId(null);
    };

    const handleSend = async () => {
        stopRef.current = false;

        if (input.trim() === "" && !uploadedFileMeta) return;

        const localFileData = uploadedFileMeta
            ? {
                file_url: uploadedFileMeta.source_url || previewUrl,
                fileType: uploadedFileMeta.mime_type || file?.type || null,
                fileName: uploadedFileMeta.name || file?.name || null,
                uploadFileId: uploadedFileMeta.id,
            }
            : null;

        const userMessage = {
            id: `user-${Date.now()}`,
            from: "user",
            text: input,
            ...(localFileData || {}),
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        scrollToBottom();

        const currentInput = input;
        const currentUploadedFileMeta = uploadedFileMeta;

        setInput("");
        setPreviewUrl(null);
        setFile(null);
        setUploadedFileMeta(null);
        setIsLoading(true);

        const payload = {
            inputs: {},
            user: userId,
            conversation_id: conversationId || "",
            query: currentInput,
            response_mode: "blocking",
            files: currentUploadedFileMeta
                ? [
                    {
                        type: currentUploadedFileMeta.mime_type?.startsWith("image/")
                            ? "image"
                            : "file",
                        transfer_method: "local_file",
                        url: "",
                        upload_file_id: currentUploadedFileMeta.id,
                    },
                ]
                : [],
        };

        try {
            let response
            if (user?.total_tokens === 0) {
                dispatch(createErrorAlert(400, '', 'Your tokens have expired. Please recharge.', 400))
                // toast.error("Your tokens have expired. Please recharge.");
                return;
            }
            // if(user?.total_tokens !== 0){
            response = await fetch(`${API_BASE}/chat-messages`, {
                method: "POST",
                body: JSON.stringify(payload),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
            });
            // }

            const res = await response.json();
            const newConversationId = res?.conversation_id;

            if (res?.task_id) setTaskId(res.task_id);

            if (newConversationId && newConversationId !== conversationId) {
                setConversationId(newConversationId);
                const url = new URL(window.location);
                url.searchParams.set("session_id", newConversationId);
                url.searchParams.delete("new");
                window.history.replaceState({}, "", url);
                onSessionCreated?.();
            }

            if (res?.answer) {
                const aiText = res.answer.replace(/<think>[\s\S]*?<\/think>/g, "").trim();

                setMessages((prev) => [
                    ...prev,
                    { id: `ai-${Date.now()}`, from: "ai", text: "" },
                ]);

                await typeText(aiText);
            } else if (res?.status === "error" || res?.error) {
                let errorText = user?.total_tokens === 0 ? 'Token expired' : res?.error || "Something went wrong. Please try again.";

                if (errorText.includes("Run failed:")) {
                    errorText = "Unable to generate response";
                }

                setMessages((prev) => [
                    ...prev,
                    {
                        id: `ai-error-${Date.now()}`,
                        from: "ai",
                        text: errorText,
                    },
                ]);
            } else {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `ai-empty-${Date.now()}`,
                        from: "ai",
                        text: "No response from AI.",
                    },
                ]);
            }
            console.log(res, "response from chat-message")
            const payloadUser = {
                username: user?.username,
                email: user?.email,
                input_tokens: res?.metadata?.usage?.prompt_tokens,
                output_tokens: res?.metadata?.usage?.completion_tokens,
                total_tokens: res?.metadata?.usage?.total_tokens,
            }
            const data = await apiRequest({
                method: 'put',
                url: 'accounts/use-tokens/',
                data: payloadUser
            }).then((resToken) => {
            }).catch((e) => {
            })
        } catch (error) {
            console.error("Error sending message:", error);
            setMessages((prev) => [
                ...prev,
                {
                    id: `ai-error-${Date.now()}`,
                    from: "ai",
                    text: "Sorry, something went wrong. Please try again.",
                },
            ]);
        } finally {
            setIsLoading(false);
            setTaskId(null);
        }
    };

    const handleStop = async () => {
        stopRef.current = true;

        if (!taskId) {
            setIsLoading(false);
            return;
        }

        try {
            await fetch(`${API_BASE}/chat-messages/${taskId}/stop`, {
                method: "POST",
                body: JSON.stringify({ user: userId }),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
            });
        } catch (e) {
            console.error("Stop error:", e);
        } finally {
            setIsLoading(false);
            setTaskId(null);
        }
    };

    const handleFileUpload = async (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);

        try {
            const res = await fetch(`${API_BASE}/files/upload`, {
                method: "POST",
                headers: {
                    Authorization: AUTH_TOKEN,
                },
                body: formData,
            });

            const data = await res.json();

            setUploadedFileMeta(data);
            setFile(selectedFile);

            if (selectedFile.type.startsWith("image/")) {
                setPreviewUrl(URL.createObjectURL(selectedFile));
            } else {
                setPreviewUrl(null);
            }
        } catch (err) {
            console.error("Upload error:", err);
        }
    };

    const handleQuickAction = (action) => {
        const prompts = {
            "Assignment help": "I need help with my assignment. Can you guide me through it?",
            "Exam preparation": "Help me prepare for my upcoming exam. What should I focus on?",
            "Summarize my lecture": "Can you help me summarize my lecture notes?",
            "Research": "I need help with research for my topic. Where should I start?",
        };
        setInput(prompts[action] || action);
    };

    return (
        <div
            style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                backgroundColor: "#f8f9fa",
            }}
        >
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "100%",
                    margin: "0 auto",
                    width: "100%",
                }}
            >
                {messages.length === 1 && (
                    <div style={{ textAlign: "center", padding: "3rem 1.5rem" }}>
                        <div
                            style={{
                                width: "4rem",
                                height: "4rem",
                                borderRadius: "9999px",
                                backgroundColor: "#CC6828",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 1.5rem",
                            }}
                        >
                            <span role="img" aria-label="bot" style={{ fontSize: "1.5rem", color: "white" }}>
                                🤖
                            </span>
                        </div>
                        <h1 style={{ fontSize: "1.875rem", fontWeight: "bold", marginBottom: "1rem" }}>
                            Hey, I'm your AI tutor, Clara!
                        </h1>
                        <p style={{ color: "#6c757d", maxWidth: "36rem", margin: "0 auto 0.5rem" }}>
                            I am trained with high-quality academic resources to provide accurate responses.
                        </p>
                        <p style={{ color: "#6c757d", maxWidth: "36rem", margin: "0 auto 1.5rem" }}>
                            I adapt to your course and offer personalized guidance to help you ace your exams!
                        </p>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "0.5rem",
                                marginBottom: "2rem",
                            }}
                        >
                            <span
                                style={{
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "9999px",
                                    padding: "0.25rem 0.75rem",
                                    fontSize: "0.875rem",
                                }}
                            >
                                C
                            </span>
                            <span
                                style={{
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "9999px",
                                    padding: "0.25rem 0.75rem",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {credits} credits
                            </span>
                        </div>
                        <div style={{ maxWidth: "36rem", margin: "0 auto" }}>
                            <p style={{ fontSize: "0.875rem", color: "#6c757d", marginBottom: "1rem" }}>
                                What can I help you with today?
                            </p>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                                    gap: "0.75rem",
                                }}
                            >
                                {["Assignment help", "Exam preparation", "Summarize my lecture", "Research"].map(
                                    (option) => (
                                        <button
                                            key={option}
                                            onClick={() => handleQuickAction(option)}
                                            style={{
                                                padding: "1rem",
                                                backgroundColor: "#fff",
                                                border: "1px solid #ccc",
                                                borderRadius: "0.5rem",
                                                cursor: "pointer",
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            {option}
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        height: messages.length === 1 ? "20vh" : "90vh",
                    }}
                >
                    <div
                        ref={messageContainer}
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            padding: "1rem 1.5rem",
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                        }}
                    >
                        {messages.map((message) => {
                            const isUser = message.from === "user";

                            return (
                                <div
                                    key={message.id}
                                    onMouseEnter={() => setHoveredId(message.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    style={{
                                        display: "flex",
                                        justifyContent: isUser ? "flex-end" : "flex-start",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    {/* LEFT SIDE (for USER messages → icons first) */}
                                    {isUser && (
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "6px",
                                                opacity: hoveredId === message.id ? 1 : 0,
                                                transition: "0.2s",
                                            }}
                                        >
                                            <FiCopy onClick={() => handleCopy(message.text)} style={{ cursor: "pointer" }} />

                                            {speakingId === message.id ? (
                                                <FaRegStopCircle
                                                    onClick={handleStopSpeak}
                                                    style={{ cursor: "pointer", color: "red" }}
                                                />
                                            ) : (
                                                <FaVolumeUp
                                                    onClick={() => handleSpeak(message.text, message.id)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* MESSAGE BUBBLE */}
                                    <div
                                        style={{
                                            maxWidth: "24rem",
                                            padding: "0.75rem 1rem",
                                            borderRadius: "0.5rem",
                                            backgroundColor: isUser ? "#CC6828" : "#e5e5ea",
                                            color: isUser ? "#fff" : "#000",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {message.text}
                                    </div>

                                    {/* RIGHT SIDE (for AI messages → icons after) */}
                                    {!isUser && (
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "6px",
                                                opacity: hoveredId === message.id ? 1 : 0,
                                                transition: "0.2s",
                                            }}
                                        >
                                            <FiCopy onClick={() => handleCopy(message.text)} style={{ cursor: "pointer" }} />

                                            {speakingId === message.id ? (
                                                <FaRegStopCircle
                                                    onClick={handleStopSpeak}
                                                    style={{ cursor: "pointer", color: "red" }}
                                                />
                                            ) : (
                                                <FaVolumeUp
                                                    onClick={() => handleSpeak(message.text, message.id)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {isLoading && (
                        <p className="mt-2 ml-4 text-sm text-primary font-bold">
                            Generating response{" "}
                            <span className="loading-dots">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                            </span>
                        </p>
                    )}

                    <div
                        style={{
                            borderTop: "1px solid #dee2e6",
                            backgroundColor: "#f8f9fa",
                            padding: "1rem",
                        }}
                    >
                        {file && (
                            <div
                                style={{
                                    display: "flex",
                                    marginBottom: "10px",
                                    justifyContent: 'center',
                                    maxWidth: "717px"
                                }}
                            >
                                <div
                                    style={{
                                        position: "relative",
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        width: "120px",
                                    }}
                                >
                                    {previewUrl ? (
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            style={{
                                                width: "100%",
                                                height: "100px",
                                                objectFit: "cover",
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                padding: "20px",
                                                background: "#eee",
                                                textAlign: "center",
                                            }}
                                        >
                                            📎
                                        </div>
                                    )}

                                    <div
                                        onClick={() => {
                                            setFile(null);
                                            setPreviewUrl(null);
                                            setUploadedFileMeta(null);
                                        }}
                                        style={{
                                            position: "absolute",
                                            top: "5px",
                                            right: "5px",
                                            background: "rgba(0,0,0,0.6)",
                                            color: "white",
                                            borderRadius: "50%",
                                            width: "22px",
                                            height: "22px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                        }}
                                    >
                                        ✕
                                    </div>
                                </div>
                            </div>
                        )}

                        <div
                            style={{
                                maxWidth: "768px",
                                margin: "0 auto",
                                display: "flex",
                                gap: "0.5rem",
                            }}
                        >
                            <input
                                type="text"
                                placeholder="Ask anything"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSend()}
                                style={{
                                    flex: 1,
                                    padding: "0.75rem",
                                    border: "1px solid #ccc",
                                    borderRadius: "0.375rem",
                                }}
                            />

                            <label className="inline-flex items-center justify-center px-3 rounded-md bg-gray-200 hover:bg-gray-300 cursor-pointer">
                                <FiUpload className="text-gray-700" />
                                <input type="file" className="hidden" onChange={handleFileUpload} />
                            </label>

                            <button
                                onClick={handleMicClick}
                                className={`px-3 rounded-md ${isRecording ? "bg-red-500 animate-pulse" : "bg-gray-200 hover:bg-gray-300"
                                    }`}
                            >
                                <FaMicrophone className={isRecording ? "text-white" : "text-gray-700"} />
                            </button>

                            {!isLoading ? (
                                <button
                                    onClick={() => {
                                        if (user?.total_tokens === 0) {
                                            dispatch(createErrorAlert(400, '', 'Your tokens have expired. Please recharge.', 400));
                                            return;
                                        }

                                        if (input.trim() === "" && !uploadedFileMeta) return;

                                        handleSend();
                                    }}
                                    disabled={(input.trim() === "" && !uploadedFileMeta)}
                                    style={{
                                        padding: "0 1rem",
                                        backgroundColor: user?.total_tokens === 0 ? "#ccc" : "#CC6828",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "0.375rem",
                                        cursor:
                                            (input.trim() === "" && !uploadedFileMeta)
                                                ? "not-allowed"
                                                : "pointer",
                                    }}
                                >
                                    <IoIosSend />
                                </button>
                            ) : (
                                <button
                                    onClick={handleStop}
                                    style={{
                                        padding: "0 1rem",
                                        backgroundColor: "#CC6828",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "0.375rem",
                                        cursor: "pointer",
                                    }}
                                >
                                    <FaRegStopCircle />
                                </button>
                            )}
                        </div>

                        <p
                            style={{
                                fontSize: "0.75rem",
                                color: "#6c757d",
                                textAlign: "center",
                                marginTop: "0.5rem",
                            }}
                        >
                            StudyCrowd.AI can make mistakes. Please check important info.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    updatedUserLoader: updatedUserLoader(state),
});
const mapDispatchToProps = (dispatch) => ({
    updateUser: (body) => dispatch(updateUser(body)),
});